import {
    billingCatalogEntity,
    billingPositionEntity,
    billingStatementEntity,
    billingStatementLineEntity,
    glossaryTermEntity,
    diaryEntity,
    drawingSheetEntity,
    findEntity,
    sampleEntity,
    findingEntity,
    findingPlanumEntity,
    findingProfileEntity,
    groupEntity,
    mediaEntity,
    mediaLinkEntity,
    mediaGroupEntity,
    overallDescriptionEntity,
    areaDescriptionEntity,
    parcelEntity,
    photoEntity,
    projectEntity,
    projectGroupEntity,
    timeEntryEntity,
    userEntity,
    userProfileEntity
} from '@ast-data/shared';
import { visibleHistoryFields } from './internalHistoryFields';
import { findHistoryLabel } from '../findings/findHistoryLabel';
import type { HistoryVersion } from '@ast-data/shared';

/**
 * What the administration's change log needs to know about each kind of record.
 * Per-record dialogs carry this at their own call site; a list spanning all
 * entities needs it in one place. The field-label namespaces are the existing
 * ones — the log labels a field exactly as the screen it came from does.
 */

export interface HistoryEntitySpec {
    /** Matches `entityType` in the history table. */
    type: string;
    /** Entity API root the history routes hang off. */
    apiBase: string;
    /** i18n id for what this kind of record is called. */
    nameId: string;
    /** Snapshot field used as a version's title in the dialog. */
    primaryField: string;
    /** The title from the whole version — see HistoryDialog's `primaryLabel`. */
    primaryLabel?: (version: HistoryVersion) => string;
    /** Snapshot fields the dialog lists, labelled `${prefix}.${key}`. */
    fieldLabelPrefix?: string;
    /** Explicit label ids for fields whose screen doesn't use a prefix. */
    fieldLabelIds?: Record<string, string>;
    /**
     * Label id for this record's Befund references. Set it and the dialog pools
     * `planumIds`/`profileIds`/`findingIds` into ONE field, each id resolved to
     * "Bef. 7000, Fl. 1, Pl. 1"; without it they read as a count.
     */
    refLabelId?: string;
    /**
     * Id fields the internal-field filter must NOT drop, because this entity's
     * membership IS the record — a team's members, a Projekt-Gruppe's
     * Maßnahmen. Without them the change reads as a bare count.
     */
    keepFields?: readonly string[];
    /** Restrict the diff to these fields (the rest isn't text-diffable). */
    onlyFields?: string[];
    /** Fields whose raw diff is an illegible object array. */
    omitFields?: string[];
    /** History that can only be read — the app doesn't author these records. */
    readOnly?: boolean;
    /** The record's own content fields, as the server snapshots them. */
    historyFields: readonly string[];
}

const SPECS: HistoryEntitySpec[] = [
    {
        type: projectEntity.name,
        apiBase: projectEntity.apiBase,
        nameId: 'history.entity.project',
        primaryField: 'measureNumber',
        fieldLabelPrefix: 'field',
        // Phasen, Kontingente and Grabungsmannschaft stay in: the change log
        // is the only place a project version is read, and historyValueFormats
        // renders them readably rather than as a bare "(geändert)".
        historyFields: projectEntity.historyFields
    },
    {
        type: parcelEntity.name,
        apiBase: parcelEntity.apiBase,
        nameId: 'history.entity.parcel',
        primaryField: 'parcelNumber',
        fieldLabelPrefix: 'field',
        historyFields: parcelEntity.historyFields
    },
    {
        type: findingEntity.name,
        apiBase: findingEntity.apiBase,
        nameId: 'history.entity.finding',
        primaryField: 'findingNumber',
        fieldLabelPrefix: 'finding.field',
        historyFields: findingEntity.historyFields
    },
    {
        type: findingPlanumEntity.name,
        apiBase: findingPlanumEntity.apiBase,
        nameId: 'history.entity.findingPlanum',
        primaryField: 'planumNumber',
        fieldLabelPrefix: 'planum.field',
        historyFields: findingPlanumEntity.historyFields
    },
    {
        type: findingProfileEntity.name,
        apiBase: findingProfileEntity.apiBase,
        nameId: 'history.entity.findingProfile',
        primaryField: 'profileNumber',
        fieldLabelPrefix: 'profile.field',
        historyFields: findingProfileEntity.historyFields
    },
    {
        type: overallDescriptionEntity.name,
        apiBase: overallDescriptionEntity.apiBase,
        nameId: 'history.entity.overallDescription',
        refLabelId: 'overallDescription.field.findings',
        primaryField: 'overallDescriptionNumber',
        fieldLabelPrefix: 'overallDescription.field',
        historyFields: overallDescriptionEntity.historyFields
    },
    {
        type: areaDescriptionEntity.name,
        apiBase: areaDescriptionEntity.apiBase,
        nameId: 'history.entity.areaDescription',
        primaryField: 'area',
        fieldLabelPrefix: 'areaDescription.field',
        historyFields: areaDescriptionEntity.historyFields
    },
    {
        type: findEntity.name,
        apiBase: findEntity.apiBase,
        nameId: 'history.entity.find',
        refLabelId: 'find.field.descriptions',
        primaryField: 'findSlipNumber',
        primaryLabel: findHistoryLabel,
        fieldLabelPrefix: 'find.field',
        historyFields: findEntity.historyFields
    },
    {
        type: sampleEntity.name,
        apiBase: sampleEntity.apiBase,
        nameId: 'history.entity.sample',
        refLabelId: 'sample.field.descriptions',
        primaryField: 'recordedAt',
        fieldLabelPrefix: 'sample.field',
        historyFields: sampleEntity.historyFields
    },
    {
        type: photoEntity.name,
        apiBase: photoEntity.apiBase,
        nameId: 'history.entity.photo',
        refLabelId: 'photo.field.findings',
        primaryField: 'fileName',
        fieldLabelPrefix: 'photo.field',
        historyFields: photoEntity.historyFields
    },
    {
        type: drawingSheetEntity.name,
        apiBase: drawingSheetEntity.apiBase,
        nameId: 'history.entity.drawingSheet',
        refLabelId: 'drawingSheet.field.findings',
        primaryField: 'drawingSheetNumber',
        fieldLabelPrefix: 'drawingSheet.field',
        historyFields: drawingSheetEntity.historyFields
    },
    {
        type: diaryEntity.name,
        apiBase: diaryEntity.apiBase,
        nameId: 'history.entity.diary',
        primaryField: 'recordedAt',
        fieldLabelPrefix: 'diary.field',
        historyFields: diaryEntity.historyFields
    },
    {
        type: timeEntryEntity.name,
        apiBase: timeEntryEntity.apiBase,
        nameId: 'history.entity.timeEntry',
        primaryField: 'workerName',
        fieldLabelPrefix: 'timeEntry.field',
        historyFields: timeEntryEntity.historyFields
    },
    {
        type: mediaEntity.name,
        apiBase: mediaEntity.apiBase,
        nameId: 'history.entity.media',
        primaryField: 'originalName',
        // Only the caption is text-diffable; the binary and the sketch scene
        // are not.
        onlyFields: ['caption'],
        fieldLabelIds: { caption: 'media.caption' },
        historyFields: mediaEntity.historyFields
    },
    {
        type: mediaLinkEntity.name,
        apiBase: mediaLinkEntity.apiBase,
        nameId: 'history.entity.mediaLink',
        primaryField: 'mediaId',
        // A link is made or removed, never edited: its fields are ids only.
        onlyFields: [],
        historyFields: mediaLinkEntity.historyFields
    },
    {
        type: mediaGroupEntity.name,
        apiBase: mediaGroupEntity.apiBase,
        nameId: 'history.entity.mediaGroup',
        primaryField: 'caption',
        // The title is what reads as a change; the layout is ids only.
        onlyFields: ['caption'],
        fieldLabelIds: { caption: 'media.group.caption' },
        historyFields: mediaGroupEntity.historyFields
    },
    {
        type: groupEntity.name,
        apiBase: groupEntity.apiBase,
        nameId: 'history.entity.group',
        primaryField: 'name',
        keepFields: ['userIds'],
        fieldLabelIds: {
            name: 'groups.name',
            description: 'groups.descriptionLabel',
            userIds: 'groups.members'
        },
        historyFields: groupEntity.historyFields
    },
    {
        type: projectGroupEntity.name,
        apiBase: projectGroupEntity.apiBase,
        nameId: 'history.entity.projectGroup',
        primaryField: 'name',
        keepFields: ['projectIds'],
        fieldLabelIds: {
            name: 'projectGroups.name',
            description: 'projectGroups.descriptionLabel',
            projectIds: 'projectGroups.members'
        },
        historyFields: projectGroupEntity.historyFields
    },
    {
        type: userEntity.name,
        apiBase: userEntity.apiBase,
        nameId: 'history.entity.user',
        primaryField: 'username',
        fieldLabelIds: {
            username: 'admin.users.username',
            displayName: 'admin.users.synologyName',
            email: 'admin.users.email',
            disabled: 'admin.users.status'
        },
        // The NAS owns these rows — nothing here to restore to.
        readOnly: true,
        historyFields: userEntity.historyFields
    },
    {
        type: userProfileEntity.name,
        apiBase: userProfileEntity.apiBase,
        nameId: 'history.entity.userProfile',
        primaryField: 'roles',
        fieldLabelIds: {
            customName: 'admin.users.customName',
            roles: 'admin.users.roles',
            crewmeisterName: 'admin.users.crewmeisterName',
            companyEmail: 'admin.users.companyEmail',
            privateEmail: 'admin.users.privateEmail',
            employmentStart: 'admin.users.employmentStart',
            employmentEnd: 'admin.users.employmentEnd',
            employeeType: 'admin.users.employeeType'
        },
        historyFields: userProfileEntity.historyFields
    },
    {
        type: glossaryTermEntity.name,
        apiBase: glossaryTermEntity.apiBase,
        nameId: 'history.entity.glossaryTerm',
        primaryField: 'de',
        fieldLabelPrefix: 'glossary',
        historyFields: glossaryTermEntity.historyFields
    },
    {
        type: billingStatementEntity.name,
        apiBase: billingStatementEntity.apiBase,
        nameId: 'history.entity.billingStatement',
        primaryField: 'title',
        keepFields: ['projectIds'],
        // The field values are an object array, unreadable as a diff; the
        // closing shows in the status.
        omitFields: ['fieldValues', 'closedAt', 'closedBy'],
        fieldLabelIds: { projectIds: 'billing.statement.projects' },
        fieldLabelPrefix: 'billing.statement',
        historyFields: billingStatementEntity.historyFields
    },
    // These three snapshot no fields: the log shows that one was made or
    // deleted, and names it from the record as it is now. Restoring lays the
    // empty snapshot over the stored row, which undoes a deletion.
    {
        type: billingStatementLineEntity.name,
        apiBase: billingStatementLineEntity.apiBase,
        nameId: 'history.entity.billingStatementLine',
        primaryField: 'positionCode',
        onlyFields: [],
        historyFields: billingStatementLineEntity.historyFields
    },
    {
        type: billingCatalogEntity.name,
        apiBase: billingCatalogEntity.apiBase,
        nameId: 'history.entity.billingCatalog',
        primaryField: 'name',
        onlyFields: [],
        historyFields: billingCatalogEntity.historyFields
    },
    {
        type: billingPositionEntity.name,
        apiBase: billingPositionEntity.apiBase,
        nameId: 'history.entity.billingPosition',
        primaryField: 'code',
        onlyFields: [],
        historyFields: billingPositionEntity.historyFields
    }
];

const BY_TYPE = new Map(SPECS.map((spec) => [spec.type, spec]));

/** Every kind of record the change log knows, in filter order. */
export const HISTORY_ENTITIES: readonly HistoryEntitySpec[] = SPECS;

/**
 * The spec for an entity type. Null is a normal answer, not a bug: the table
 * still holds legacy `entry` versions, and an entity with history but no spec
 * should show as a row with a bare type rather than break the list.
 */
export function historyEntity(type: string): HistoryEntitySpec | null {
    return BY_TYPE.get(type) ?? null;
}

/** In recorded order. */
export function historyDialogFields(spec: HistoryEntitySpec): string[] {
    if (spec.onlyFields) {
        return [...spec.onlyFields];
    }
    return visibleHistoryFields(spec.historyFields, spec.keepFields).filter(
        (key) => !spec.omitFields?.includes(key)
    );
}

/** The i18n id labelling one snapshot field ('' when the entity names none). */
export function historyFieldLabelId(
    spec: HistoryEntitySpec,
    key: string
): string {
    return (
        spec.fieldLabelIds?.[key] ??
        (spec.fieldLabelPrefix ? `${spec.fieldLabelPrefix}.${key}` : '')
    );
}
